import { useCallback, useRef } from "react";

/**
 * useLongPress — touch/mouse gesture hook with tap / double-tap / long-press.
 *
 * Gesture matrix:
 *   - tap                 → onTap         (primary action, e.g. +2h)
 *   - double-tap          → onDoubleTap   (opt-in, e.g. +8h)
 *   - long-press (>500ms) → onLongPress   (tertiary action, e.g. clear/delete)
 *
 * When `onDoubleTap` is provided, `onTap` is deferred ~260ms after touchend so
 * that a potential second tap can be detected. If `onDoubleTap` is omitted, the
 * hook keeps the legacy immediate-tap behavior (no regression on callers that
 * only use onTap + onLongPress).
 *
 * Scroll cancellation: gesture is aborted if the finger moves > 10px.
 */
export function useLongPress({
  onTap,
  onLongPress,
  onDoubleTap,
  thresholdMs = 500,
  doubleTapMs = 260,
  moveCancelPx = 10,
} = {}) {
  const timerRef = useRef(null);
  const tapTimerRef = useRef(null);
  const triggeredRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const lastTapAtRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearTapTimer = useCallback(() => {
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
    }
  }, []);

  const start = useCallback((evt) => {
    triggeredRef.current = false;
    const point = evt.touches ? evt.touches[0] : evt;
    startPosRef.current = { x: point.clientX, y: point.clientY };
    timerRef.current = setTimeout(() => {
      triggeredRef.current = true;
      timerRef.current = null;
      // Long-press pre-empts any pending tap (user kept finger down).
      clearTapTimer();
      onLongPress && onLongPress(evt);
    }, thresholdMs);
  }, [onLongPress, thresholdMs, clearTapTimer]);

  const move = useCallback((evt) => {
    if (!timerRef.current) return;
    const point = evt.touches ? evt.touches[0] : evt;
    const dx = Math.abs(point.clientX - startPosRef.current.x);
    const dy = Math.abs(point.clientY - startPosRef.current.y);
    if (dx > moveCancelPx || dy > moveCancelPx) clearTimer();
  }, [moveCancelPx, clearTimer]);

  // Resolve a confirmed tap. Handles double-tap detection when onDoubleTap is provided.
  const fireTap = useCallback((evt) => {
    if (!onDoubleTap) {
      // Legacy path — fire immediately.
      onTap && onTap(evt);
      return;
    }
    const now = Date.now();
    const delta = now - lastTapAtRef.current;
    if (delta > 0 && delta < doubleTapMs) {
      // Second tap arrived in time → double-tap. Cancel any pending first tap.
      lastTapAtRef.current = 0;
      clearTapTimer();
      onDoubleTap(evt);
      return;
    }
    // First tap — defer onTap in case a second tap follows shortly.
    lastTapAtRef.current = now;
    clearTapTimer();
    tapTimerRef.current = setTimeout(() => {
      tapTimerRef.current = null;
      lastTapAtRef.current = 0;
      onTap && onTap(evt);
    }, doubleTapMs);
  }, [onTap, onDoubleTap, doubleTapMs, clearTapTimer]);

  const end = useCallback((evt) => {
    if (timerRef.current) {
      clearTimer();
      // Timer hadn't fired yet → treat as a tap (or first half of a double-tap).
      if (evt.cancelable !== false) {
        try { evt.preventDefault(); } catch (_e) { /* swallow */ }
      }
      fireTap(evt);
    }
  }, [clearTimer, fireTap]);

  const cancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  // Hybrid devices (tablet with mouse) fire a synthetic mouse click ~300ms after touchend.
  // We ignore those by tracking touchend timestamp.
  const lastTouchEndAtRef = useRef(0);
  const onTouchEndWrapped = useCallback((evt) => {
    end(evt);
    lastTouchEndAtRef.current = Date.now();
  }, [end]);

  return {
    onTouchStart: start,
    onTouchMove: move,
    onTouchEnd: onTouchEndWrapped,
    onTouchCancel: cancel,
    onContextMenu: (e) => {
      e.preventDefault();
      onLongPress && onLongPress(e);
    },
    // Desktop click. On touch devices the synthetic click fires right after touchend; we
    // ignore it via the timestamp guard.
    onClick: (e) => {
      if (Date.now() - lastTouchEndAtRef.current < 700) return;
      fireTap(e);
    },
    // Desktop double-click → double-tap equivalent. Guarded by the same window so touch
    // devices don't fire it twice.
    onDoubleClick: (e) => {
      if (Date.now() - lastTouchEndAtRef.current < 700) return;
      // Cancel any pending deferred single-tap that fireTap may have scheduled.
      clearTapTimer();
      lastTapAtRef.current = 0;
      if (onDoubleTap) { e.preventDefault(); onDoubleTap(e); }
    },
  };
}
